import { coordinateToChartTime } from '../../../lib/chart/chartTimeProjection.js'
import { chartTimeLabelFormatter } from '../../../lib/chart/chartTimeLabel.js'
import { CurrentPriceLabelPrimitive } from '../../../lib/chart/CurrentPriceLabelPrimitive.js'
import { useChartNavigationCommands } from '../useChartNavigationCommands.js'
import { applySyncedCrosshair, clearSyncedCrosshairOverlay } from '../../../lib/chart/crosshairSync.js'
import { chartMarginsForTimeframe, normalizeChartMarginSlot, applyDefaultVisibleLogicalRange } from '../../../lib/chart/chartMargins.js'
import { shouldShieldKeyboardEvent, isEditableTarget } from '../../../commands/commandModel.js'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { createChart } from '../../../lib/chart/createChartCompat.js'
import { updateChartSeries } from '../../../lib/marketData/marketHealthSeries.js'
import { CHART_LAYOUT_WORKSPACE, CHART_GRID_WORKSPACE, CHART_CROSSHAIR_WORKSPACE, CHART_RIGHT_PRICE_SCALE_WORKSPACE, CHART_TIME_SCALE_WORKSPACE, SERIES_COLORS_WORKSPACE, BORDER_COLOR_RGBA, BORDER_COLOR_RGBA_HOVER } from '../chartTokens.js'
import RsChartSettings from './RsChartSettings.jsx'
import { normalizeRsAppearance, lineColor, linePoints } from './lineAppearance.js'
import ChartDrawings from './ChartDrawings.jsx'
import { useRsCandles } from './useRsCandles.js'

const ROLES = ['asset', 'benchmark']
export default forwardRef(function RelativeStrengthCharts({ userId, chartProps, benchmark = 'SPY', timeframe, marketSession, tool, onToolChange,
  activeRole = 'asset', onActiveRoleChange, onCrosshairMove, keyboardActive = true, colors = {}, appearance, onAppearanceChange, marginsByTimeframe, onMarginsChange }, ref) {
  const container = useRef(null), current = useRef(null), data = useRef({}), initialView = useRef(null)
  const [apis, setApis] = useState(null), [geometry, setGeometry] = useState([]), [settings, setSettings] = useState(false)
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'))
  const theme = dark ? 'dark' : 'light'
  const resolvedAppearance = normalizeRsAppearance(appearance, colors)
  const styles = resolvedAppearance[theme]
  const lineStyleKey = JSON.stringify(ROLES.map(role => {
    const { color, endColor, gradient, opacity, width } = styles[role]
    return { color, endColor, gradient, opacity, width }
  }))
  const labelStyleKey = JSON.stringify([styles.labels, ...ROLES.map(role => {
    const { currentPriceLabelCustom, currentPriceLabelColor, lastPriceVisible } = styles[role]
    return { currentPriceLabelCustom, currentPriceLabelColor, lastPriceVisible }
  })])
  const margins = chartMarginsForTimeframe({ marginsByTimeframe }, timeframe)
  const marginsKey = JSON.stringify(margins)
  const resetView = useCallback(() => {
    if (!apis) return
    const times = new Set(ROLES.flatMap(role => (data.current[role] || []).map(point => point.time)))
    ROLES.forEach(role => apis[role].series.priceScale().applyOptions({ autoScale: true, scaleMargins: { top: margins.top, bottom: margins.bottom } }))
    applyDefaultVisibleLogicalRange(apis.asset.chart, times.size, margins.visibleBars, margins.rightOffset)
  }, [apis, marginsKey])
  const updateMargins = patch => {
    const next = { ...margins }
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === '') continue
      if (key === 'topPercent') next.top = Number(value) / 100
      else if (key === 'bottomPercent') next.bottom = Number(value) / 100
      else next[key] = Number(value)
    }
    onMarginsChange({ ...marginsByTimeframe, [timeframe]: normalizeChartMarginSlot(next, timeframe) })
  }
  useEffect(() => { resetView() }, [resetView])
  useEffect(() => {
    const keydown = event => {
      if (!keyboardActive || event.defaultPrevented || shouldShieldKeyboardEvent(event) || isEditableTarget(document.activeElement) || document.querySelector('[aria-modal="true"]')) return
      if (!event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) return
      if (String(event.key).toLowerCase() !== 'r' && event.code !== 'KeyR') return
      event.preventDefault()
      resetView()
    }
    window.addEventListener('keydown', keydown)
    return () => window.removeEventListener('keydown', keydown)
  }, [keyboardActive, resetView])
  const drawings = { asset: useRef(null), benchmark: useRef(null) }
  const asset = useRsCandles({ symbol: chartProps.symbol, timeframe, marketSession, chartProps, role: 'asset' })
  const comparison = useRsCandles({ symbol: benchmark, timeframe, marketSession, chartProps, role: 'benchmark' })
  useChartNavigationCommands({
    active: keyboardActive, containerRef: container,
    getChart: () => apis?.asset.chart,
    getLatestTime: () => data.current[activeRole]?.at(-1)?.time,
    ready: (activeRole === 'asset' ? asset : comparison).status === 'ready', openSettings: () => setSettings(true),
  })
  const sources = { asset, benchmark: comparison }
  current.current = { sources, activeRole, onActiveRoleChange, onCrosshairMove, timeframe }
  useImperativeHandle(ref, () => ({ drawingAction: action => drawings[activeRole].current?.[action]?.(), openSettings: () => setSettings(true), resetView,
    setSyncedCrosshair: point => applySyncedCrosshair(apis?.asset.chart, apis?.asset.series,
      asset.status === 'ready' ? point : null, timeframe, [apis?.benchmark.series]),
  }))
  useEffect(() => {
    const observer = new MutationObserver(() => setDark(document.documentElement.classList.contains('dark')))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    const node = container.current
    const chart = createChart(node, {
      width: node.clientWidth || 800, height: node.clientHeight || 480,
      layout: { ...CHART_LAYOUT_WORKSPACE, attributionLogo: false, panes: { enableResize: true, separatorColor: BORDER_COLOR_RGBA, separatorHoverColor: BORDER_COLOR_RGBA_HOVER } },
      grid: CHART_GRID_WORKSPACE, crosshair: CHART_CROSSHAIR_WORKSPACE,
      rightPriceScale: { ...CHART_RIGHT_PRICE_SCALE_WORKSPACE, mode: 0 },
      timeScale: { ...CHART_TIME_SCALE_WORKSPACE, rightOffset: 5 },
    })
    const pair = Object.fromEntries(ROLES.map((role, paneIndex) => [role, { chart, paneIndex, manageInteraction: false,
      series: chart.addLineSeries({ color: SERIES_COLORS_WORKSPACE.barUp, lineWidth: 1, pointMarkersVisible: true, pointMarkersRadius: 3, crosshairMarkerVisible: false }, paneIndex) }]))
    chart.panes().forEach(pane => pane.setStretchFactor(1))
    let frame, disposed = false
    const measurePanes = () => {
      if (disposed) return
      const outer = node.getBoundingClientRect()
      setGeometry(chart.panes().map(pane => { const box = pane.getHTMLElement().getBoundingClientRect(); return { top: box.top - outer.top, height: pane.getHeight() } }))
    }
    const paneResize = new ResizeObserver(measurePanes)
    let observedPanes = false
    const layout = () => {
      if (disposed) return
      // Keep hidden peers at their last size; paint visible resizes in this
      // ResizeObserver callback so the old canvas cannot flash over new panes.
      const width = node.clientWidth, height = node.clientHeight
      if (width > 0 && height > 0) chart.resize(width, height, true)
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        if (disposed) return
        if (chart.panes().some(pane => !pane.getHTMLElement())) { frame = requestAnimationFrame(layout); return }
        setApis(pair)
        if (!observedPanes) {
          chart.panes().forEach(pane => {
            const element = pane.getHTMLElement()
            element.style.cursor = 'crosshair'
            paneResize.observe(element)
          })
          observedPanes = true
        }
        measurePanes()
      })
    }
    const resize = new ResizeObserver(layout); resize.observe(node); layout()
    const activate = event => {
      chart.panes().forEach((pane, index) => { if (pane.getHTMLElement()?.contains(event.target)) current.current.onActiveRoleChange(ROLES[index]) })
    }
    node.addEventListener('pointerdown', activate)
    const crosshairMove = event => {
      // Native programmatic updates have no sourceEvent; do not echo them.
      if (!event.sourceEvent) return
      const role = ROLES[event.paneIndex ?? 0]
      const state = current.current
      ROLES.forEach(key => clearSyncedCrosshairOverlay(pair[key].series))
      const time = event.time ?? (role ? coordinateToChartTime(chart, pair[role].series, event.point?.x, state.timeframe) : null)
      if (!role || state.sources[role].status !== 'ready' || time == null || !event.point || event.point.x < 0 || event.point.y < 0) {
        state.onCrosshairMove?.(null)
        return
      }
      // Benchmark prices belong to a different asset. Sync its time only;
      // regular charts will use their own matching candle's close.
      const price = role === 'asset' ? pair.asset.series.coordinateToPrice(event.point.y) : undefined
      state.onCrosshairMove?.({ time, timeframe: state.timeframe, price, projected: event.time == null })
    }
    const clearCrosshair = () => current.current.onCrosshairMove?.(null)
    chart.subscribeCrosshairMove(crosshairMove)
    node.addEventListener('pointerleave', clearCrosshair)
    let loading = false, previousFrom = null
    const scrollback = range => {
      const movedLeft = previousFrom != null && range && range.from < previousFrom - 1
      previousFrom = range?.from ?? null
      if (!movedLeft || range.from > 25 || loading) return
      loading = true
      Promise.allSettled(ROLES.map(role => current.current.sources[role].loadMoreBefore?.())).finally(() => { loading = false })
    }
    chart.timeScale().subscribeVisibleLogicalRangeChange(scrollback)
    const scale = chart.timeScale()
    let markersVisible = true
    const updateMarkers = () => {
      const start = scale.logicalToCoordinate(0), next = scale.logicalToCoordinate(1)
      if (start == null || next == null) return
      // Native 3px-radius dots need a small gap to stay distinct.
      const visible = Math.abs(next - start) >= 8
      if (visible === markersVisible) return
      markersVisible = visible
      ROLES.forEach(role => pair[role].series.applyOptions({ pointMarkersVisible: visible }))
    }
    scale.subscribeVisibleLogicalRangeChange(updateMarkers)
    scale.subscribeSizeChange(updateMarkers)
    return () => { disposed = true; cancelAnimationFrame(frame); resize.disconnect(); paneResize.disconnect(); node.removeEventListener('pointerdown', activate); node.removeEventListener('pointerleave', clearCrosshair); chart.unsubscribeCrosshairMove(crosshairMove); scale.unsubscribeVisibleLogicalRangeChange(scrollback); scale.unsubscribeVisibleLogicalRangeChange(updateMarkers); scale.unsubscribeSizeChange(updateMarkers); setApis(null); chart.remove(); data.current = {}; initialView.current = null }
  }, [])
  useEffect(() => {
    if (!apis) return
    const chart = apis.asset.chart
    // Copy native options before applying: LWC mutates its option objects.
    const previous = structuredClone({ handleScroll: chart.options().handleScroll, handleScale: chart.options().handleScale })
    if (!['select','pen','eraser'].includes(tool)) chart.applyOptions({ handleScroll: false })
    return () => { try { chart.applyOptions(previous) } catch { /* disposed */ } }
  }, [apis, tool])
  useEffect(() => {
    if (!apis) return
    ROLES.forEach(role => apis[role].series.applyOptions({ color: lineColor(styles[role].color, styles[role].opacity), lineWidth: styles[role].width }))
  }, [apis, lineStyleKey])
  useEffect(() => {
    if (!apis) return
    apis.asset.chart.applyOptions({ crosshair: {
      vertLine: { labelBackgroundColor: styles.labels.dateLabelColor || CHART_CROSSHAIR_WORKSPACE.vertLine.labelBackgroundColor },
      horzLine: { labelBackgroundColor: styles.labels.crosshairPriceLabelColor || CHART_CROSSHAIR_WORKSPACE.horzLine.labelBackgroundColor },
    } })
    const attached = []
    ROLES.forEach(role => {
      const { series } = apis[role], style = styles[role]
      series.applyOptions({ lastValueVisible: style.lastPriceVisible && !style.currentPriceLabelCustom })
      if (!style.lastPriceVisible || !style.currentPriceLabelCustom) return
      const primitive = new CurrentPriceLabelPrimitive(style.currentPriceLabelColor)
      series.attachPrimitive(primitive)
      attached.push({ series, primitive })
    })
    return () => attached.forEach(({ series, primitive }) => {
      try { series.detachPrimitive(primitive) } catch (error) {
        if (error.message !== 'Object is disposed') throw error
      }
    })
  }, [apis, labelStyleKey])
  useEffect(() => {
    if (!apis) return
    const chart = apis.asset.chart, scale = chart.timeScale()
    chart.applyOptions({
      localization: { timeFormatter: chartTimeLabelFormatter(timeframe) },
      timeScale: { timeVisible: !['daily', 'weekly'].includes(timeframe) },
    })
    const key = JSON.stringify([chartProps.symbol, benchmark, timeframe, marketSession])
    const oldRange = scale.getVisibleRange()
    const oldLogical = scale.getVisibleLogicalRange()
    const oldFirst = ROLES.flatMap(role => data.current[role] || []).reduce((min, p) => Math.min(min, p.time), Infinity)
    for (const role of ROLES) {
      const next = linePoints(sources[role].bars, styles[role])
      updateChartSeries(apis[role].series, data.current[role] || [], next)
      data.current[role] = next
    }
    if (asset.status === 'ready' && comparison.status === 'ready' && initialView.current !== key) {
      const times = [...new Set([...asset.bars, ...comparison.bars].map(bar => bar.time))].sort((a, b) => a - b)
      if (times.length) { resetView(); initialView.current = key }
    } else if (initialView.current === key && oldRange && oldLogical) {
      // Backfill adds dates to the single clock. Preserve the viewed dates,
      // rather than shifting one chart by its own (different) history count.
      const newTimes = [...new Set(ROLES.flatMap(role => data.current[role].map(p => p.time)))].sort((a, b) => a - b)
      const prepended = newTimes.filter(time => time < oldFirst).length
      if (prepended) scale.setVisibleLogicalRange({ from: oldLogical.from + prepended, to: oldLogical.to + prepended })
    }
  }, [apis, asset.bars, comparison.bars, asset.status, comparison.status, chartProps.symbol, benchmark, timeframe, marketSession, lineStyleKey, resetView])
  return <div role="region" aria-label="Relative strength charts" className="absolute inset-0 bg-canvas">
    <div ref={container} data-testid="rs-shared-chart" className="absolute inset-0" />
    {ROLES.map((role, index) => <div key={role} data-testid={`rs-${role}-pane`} className="pointer-events-none absolute left-0 right-0 [&>*]:pointer-events-auto"
      onPointerDown={() => onActiveRoleChange(role)} style={{ top: geometry[index]?.top || 0, height: geometry[index]?.height || 0 }}>
      {sources[role].status !== 'ready' || !sources[role].bars.length ? <div role="status" className="absolute inset-0 flex items-center justify-center bg-canvas/90 text-xs text-inkMuted">
        {sources[role].status === 'error' ? <button type="button" onClick={sources[role].retry}>Chart unavailable. Retry</button> : sources[role].status === 'ready' ? 'No chart data' : 'Loading chart…'}
      </div> : null}
      {sources[role].connection === 'unavailable' && <span role="status" className="absolute left-2 top-2 rounded bg-canvas/90 px-2 py-1 text-xs text-inkMuted">Chart data {sources[role].connection}</span>}
      <ChartDrawings key={JSON.stringify([userId, role === 'asset' ? chartProps.symbol : benchmark, marketSession])} ref={drawings[role]} userId={userId}
        storageKey={JSON.stringify([role === 'asset' ? chartProps.symbol : benchmark, marketSession])} drawingView="rs" timeframe={timeframe} theme={theme} api={apis?.[role]}
        tool={tool} active={activeRole === role} keyboardActive={keyboardActive} onActivate={() => onActiveRoleChange(role)} onToolChange={onToolChange} />
    </div>)}
    {settings && <RsChartSettings symbols={{ asset: chartProps.symbol, benchmark }} theme={theme}
      timeframe={timeframe} margins={margins} onMarginsChange={updateMargins} onResetView={resetView}
      appearance={resolvedAppearance} onChange={onAppearanceChange} onClose={() => setSettings(false)} />}
  </div>
})
